import { Linking, StyleSheet, Text, TouchableOpacity, View, type ViewStyle } from 'react-native'
import { clubBranding, clubPhoneTelHref } from '../config/clubBranding'
import { COLORS, SPACING, TYPOGRAPHY } from '../constants/theme'
import { useAppTranslation } from '../i18n/useAppTranslation'
import {
    InstagramContactIcon,
    PhoneContactIcon,
    TelegramContactIcon,
} from './clubContact/ClubContactIcons'

export type ClubContactLinksVariant = 'onLight' | 'onDark'

type ClubContactLinksProps = {
    style?: ViewStyle
    /**
     * Contrast flag:
     * - `onDark` — white links for green/felt splash & auth backgrounds
     * - `onLight` — dark links for light card surfaces
     */
    variant?: ClubContactLinksVariant
}

type ContactItem = {
    key: string
    label: string
    href: string
    accessibilityLabel: string
    icon: 'phone' | 'instagram' | 'telegram'
}

const openHref = (href: string) => {
    Linking.openURL(href).catch(() => undefined)
}

const ICON_SIZE = 20

const ClubContactLinks = ({ style, variant = 'onLight' }: ClubContactLinksProps) => {
    const { t } = useAppTranslation()
    const onDark = variant === 'onDark'
    const textColor = onDark ? COLORS.white : COLORS.gray900

    const items: ContactItem[] = []
    if (clubBranding.contactPhone) {
        const tel = clubPhoneTelHref(clubBranding.contactPhone)
        if (tel) {
            items.push({
                key: 'phone',
                label: clubBranding.contactPhone,
                href: tel,
                accessibilityLabel: t('clubContact.callPhone', { phone: clubBranding.contactPhone }),
                icon: 'phone',
            })
        }
    }
    if (clubBranding.instagramUrl) {
        items.push({
            key: 'instagram',
            label: t('clubContact.instagram'),
            href: clubBranding.instagramUrl,
            accessibilityLabel: t('clubContact.instagram'),
            icon: 'instagram',
        })
    }
    if (clubBranding.telegramUrl) {
        items.push({
            key: 'telegram',
            label: t('clubContact.telegram'),
            href: clubBranding.telegramUrl,
            accessibilityLabel: t('clubContact.telegram'),
            icon: 'telegram',
        })
    }
    if (clubBranding.telegramChatUrl) {
        items.push({
            key: 'telegramChat',
            label: t('clubContact.telegramChat'),
            href: clubBranding.telegramChatUrl,
            accessibilityLabel: t('clubContact.telegramChatA11y'),
            icon: 'telegram',
        })
    }

    if (items.length === 0) {
        return null
    }

    const renderIcon = (icon: ContactItem['icon']) => {
        if (icon === 'phone') {
            return <PhoneContactIcon size={ICON_SIZE} color={textColor} />
        }
        if (icon === 'instagram') {
            return <InstagramContactIcon size={ICON_SIZE} />
        }
        return <TelegramContactIcon size={ICON_SIZE} />
    }

    return (
        <View style={[styles.row, style]}>
            {items.map((item, index) => (
                <View key={item.key} style={styles.itemWrap}>
                    {index > 0 ? (
                        <Text style={[styles.separator, { color: onDark ? 'rgba(255,255,255,0.5)' : COLORS.gray500 }]}>
                            ·
                        </Text>
                    ) : null}
                    <TouchableOpacity
                        style={styles.linkRow}
                        onPress={() => openHref(item.href)}
                        accessibilityRole="link"
                        accessibilityLabel={item.accessibilityLabel}
                        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    >
                        {renderIcon(item.icon)}
                        <Text style={[styles.link, { color: textColor }]}>{item.label}</Text>
                    </TouchableOpacity>
                </View>
            ))}
        </View>
    )
}

const styles = StyleSheet.create({
    row: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        gap: SPACING.sm,
    },
    itemWrap: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.sm,
    },
    linkRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.xs,
    },
    separator: {
        fontSize: TYPOGRAPHY.fontSize.lg,
        includeFontPadding: false,
    },
    link: {
        fontSize: TYPOGRAPHY.fontSize.lg,
        fontWeight: TYPOGRAPHY.fontWeight.semibold,
        textDecorationLine: 'underline',
        includeFontPadding: false,
    },
})

export default ClubContactLinks
